package com.ragnorak.clanybackoffice

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.lifecycle.viewmodel.compose.viewModel

import com.ragnorak.clanybackoffice.ui.screens.DashboardViewModel
import com.ragnorak.clanybackoffice.ui.screens.UsersViewModel
import com.ragnorak.clanybackoffice.ui.screens.AccountsViewModel
import com.ragnorak.clanybackoffice.ui.screens.MembersViewModel
import com.ragnorak.clanybackoffice.ui.screens.SubscriptionsViewModel
import com.ragnorak.clanybackoffice.ui.screens.SettingsViewModel
import com.ragnorak.clanybackoffice.ui.screens.SubscriptionPlansViewModel
import com.ragnorak.clanybackoffice.ui.screens.MetadataViewModel
import com.ragnorak.clanybackoffice.ui.screens.WebhookEventsViewModel
import com.ragnorak.clanybackoffice.ui.screens.InvitationsViewModel
import dev.zacsweers.metro.createGraph
import com.ragnorak.clanybackoffice.di.ClientGraph
import com.ragnorak.clanybackoffice.ui.components.Sidebar
import com.ragnorak.clanybackoffice.ui.screens.LoginScreen
import com.ragnorak.clanybackoffice.ui.screens.UsersScreen
import com.ragnorak.clanybackoffice.ui.screens.AccountsScreen
import com.ragnorak.clanybackoffice.ui.screens.DashboardScreen
import com.ragnorak.clanybackoffice.ui.screens.MembersScreen
import com.ragnorak.clanybackoffice.ui.screens.SubscriptionsScreen
import com.ragnorak.clanybackoffice.ui.screens.SettingsScreen
import com.ragnorak.clanybackoffice.ui.screens.SubscriptionPlansScreen
import com.ragnorak.clanybackoffice.ui.screens.MetadataScreen
import com.ragnorak.clanybackoffice.ui.screens.WebhookEventsScreen
import com.ragnorak.clanybackoffice.ui.screens.InvitationsScreen
import com.ragnorak.clanybackoffice.ui.screens.NotificationsScreen
import com.ragnorak.clanybackoffice.ui.theme.ClanyTheme
import androidx.navigation3.runtime.NavEntry
import androidx.navigation3.ui.NavDisplay
import kotlinx.serialization.Serializable
import com.clany.backoffice.data.network.cleanOAuthParams
import com.clany.backoffice.data.network.getSessionItem
import com.clany.backoffice.data.network.getUrlParam
import com.clany.backoffice.data.network.removeSessionItem
import kotlinx.coroutines.launch

@Serializable
sealed interface Route {
    @Serializable data object Login : Route
    @Serializable data object Dashboard : Route
    @Serializable data object Users : Route
    @Serializable data object Accounts : Route
    @Serializable data object Members : Route
    @Serializable data object Subscriptions : Route
    @Serializable data object SubscriptionPlans : Route
    @Serializable data object Metadata : Route
    @Serializable data object WebhookEvents : Route
    @Serializable data object Invitations : Route
    @Serializable data object Notifications : Route
    @Serializable data object Settings : Route
}

@Composable
fun App() {
    val graph = remember { createGraph<ClientGraph>() }

    ClanyTheme {
        val scope = rememberCoroutineScope()
        val authState by graph.authRepository.authState.collectAsState()

        // Detect OAuth callback synchronously before first composition
        val oauthCode = remember { getUrlParam("code") }
        val isLoggedOut = remember { getSessionItem("logged_out") == "true" }
        var isHandlingOAuth by remember { mutableStateOf(oauthCode != null) }
        var oauthError by remember { mutableStateOf<String?>(null) }

        LaunchedEffect(Unit) {
            if (isLoggedOut) removeSessionItem("logged_out")
            if (oauthCode != null) {
                cleanOAuthParams()
                graph.authRepository.handleOAuthCode(oauthCode)
                    .onFailure { oauthError = it.message }
                isHandlingOAuth = false
            }
        }

        if (isHandlingOAuth) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
            return@ClanyTheme
        }

        val backStack = remember {
            mutableStateListOf<Route>(if (authState) Route.Dashboard else Route.Login)
        }

        LaunchedEffect(authState) {
            if (!authState && backStack.lastOrNull() !is Route.Login) {
                backStack.clear()
                backStack.add(Route.Login)
            } else if (authState && backStack.lastOrNull() is Route.Login) {
                backStack.clear()
                backStack.add(Route.Dashboard)
            }
        }

        if (backStack.isEmpty()) {
            return@ClanyTheme
        }

        val currentRoute = backStack.last()

        Row(modifier = Modifier.fillMaxSize()) {
            if (currentRoute !is Route.Login) {
                Sidebar(
                    currentRoute = currentRoute,
                    onNavigate = { route ->
                        if (backStack.last() != route) {
                            if (route is Route.Dashboard) {
                                backStack.clear()
                                backStack.add(Route.Dashboard)
                            } else {
                                if (backStack.size > 1) backStack.removeLast()
                                backStack.add(route)
                            }
                        }
                    },
                    onLogout = {
                        scope.launch { graph.authRepository.logout() }
                    }
                )
            }

            NavDisplay(
                backStack = backStack,
                onBack = { if (backStack.size > 1) backStack.removeLast() },
                modifier = Modifier.weight(1f),
                entryProvider = { key ->
                    when (key) {
                        is Route.Login -> NavEntry(key) {
                            LoginScreen(
                                authRepository = graph.authRepository,
                                oauthError = oauthError,
                                isLoggedOut = isLoggedOut
                            )
                        }
                        is Route.Dashboard -> NavEntry(key) {
                            val viewModel = viewModel { graph.dashboardViewModel }
                            DashboardScreen(viewModel = viewModel)
                        }
                        is Route.Users -> NavEntry(key) {
                            val viewModel = viewModel { graph.usersViewModel }
                            UsersScreen(
                                viewModel = viewModel,
                                onEditUser = { user ->
                                    // TODO: Show edit dialog
                                }
                            )
                        }
                        is Route.Accounts -> NavEntry(key) {
                            val viewModel = viewModel { graph.accountsViewModel }
                            AccountsScreen(viewModel = viewModel)
                        }
                        is Route.Members -> NavEntry(key) {
                            val viewModel = viewModel { graph.membersViewModel }
                            MembersScreen(viewModel = viewModel)
                        }
                        is Route.Subscriptions -> NavEntry(key) {
                            val viewModel = viewModel { graph.subscriptionsViewModel }
                            SubscriptionsScreen(viewModel = viewModel)
                        }
                        is Route.SubscriptionPlans -> NavEntry(key) {
                            val viewModel = viewModel { graph.subscriptionPlansViewModel }
                            SubscriptionPlansScreen(viewModel = viewModel)
                        }
                        is Route.Metadata -> NavEntry(key) {
                            val viewModel = viewModel { graph.metadataViewModel }
                            MetadataScreen(viewModel = viewModel)
                        }
                        is Route.WebhookEvents -> NavEntry(key) {
                            val viewModel = viewModel { graph.webhookEventsViewModel }
                            WebhookEventsScreen(viewModel = viewModel)
                        }
                        is Route.Invitations -> NavEntry(key) {
                            val viewModel = viewModel { graph.invitationsViewModel }
                            InvitationsScreen(viewModel = viewModel)
                        }
                        is Route.Notifications -> NavEntry(key) {
                            val viewModel = viewModel { graph.notificationsViewModel }
                            NotificationsScreen(viewModel = viewModel)
                        }
                        is Route.Settings -> NavEntry(key) {
                            val viewModel = viewModel { graph.settingsViewModel }
                            SettingsScreen(viewModel = viewModel)
                        }
                    }
                }
            )
        }
    }
}